import { useQuery } from '@tanstack/react-query';
import { PkJersey, PkIdentifier } from '@uefadigital/panenka-react';
import clsx from 'clsx';
import { memo, useMemo, useRef } from 'react';

import GptBanner from '../../GptBanner';
import { useFitty } from '../../hooks/useFitty';
import { usePlayerLinkRule } from '../../hooks/useLinkRule';
import TeamIdentifier from '../../Identifier/TeamIdentifier';
import useMatch from '../../MatchUnit/useMatch';
import { getMatchEventsQuery, getMatchLineupsQuery, getMatchPlayersStatusQuery } from '../../queries/matches';
import { useTranslateFn } from '../../Translate';

import SubstitutionCarousel from './SubstitutionCarousel';
import {
  getCoachPerson,
  getCoachRole,
  getEventsByTypes,
  getPlayerPosition,
  getPlayerGoals,
  getPlayerCards,
  getPlayerSubstitutions,
  showTacticalLineups,
  splitUnavailablePlayersToTeams,
} from './utils';

import { EventsFilter } from '@apis/events';
import { hydrateWithTranslations } from '@services/TranslatorService';
import type { LineupsPlayer, LineupsTeam, Match, PlayerEvent } from '@shared-types/match.types';
import { EventType, MatchStatus } from '@shared-types/match.types';
import { IS_TEST } from '@utilities/browser';
import { getRefreshRate } from '@utilities/live';

import * as classes from './styles.module.scss';

const trackingData = {
  category: 'lineups',
};

const LineupPlayerLinked = (props: {
  lnPlayer: LineupsPlayer;
  events: PlayerEvent[];
  match: Match;
  isField?: boolean;
  isBench?: boolean;
  teamColorShirt: string;
  identifierType: 'vertical' | 'horizontal';
  disablePlayerLink?: boolean;
}) => {
  const { lnPlayer, match, disablePlayerLink } = props;
  const player = useMemo(() => hydrateWithTranslations(lnPlayer.player), [lnPlayer]);
  const playerLink = usePlayerLinkRule(player.id, match.competition.id, match.round?.phase);

  return disablePlayerLink ? (
    <LineupPlayer {...props} />
  ) : (
    <a title={player.displayName ?? player.name} href={playerLink}>
      <LineupPlayer {...props} />
    </a>
  );
};

const LineupPlayer = ({
  lnPlayer,
  events,
  isField,
  isBench,
  teamColorShirt,
  identifierType,
}: {
  lnPlayer: LineupsPlayer;
  events: PlayerEvent[];
  isField?: boolean;
  isBench?: boolean;
  teamColorShirt: string;
  identifierType: 'vertical' | 'horizontal';
}) => {
  const playerNameRef = useRef<HTMLDivElement>(null);

  const t = useTranslateFn();
  const gk = t('goalkeeper_abbr');
  const c = t('captain_abbr');
  const player = useMemo(() => hydrateWithTranslations(lnPlayer.player), [lnPlayer]);

  const isCaptain = useMemo(() => ['CAPTAIN', 'GOALKEEPER_CAPTAIN'].includes(lnPlayer.type), [lnPlayer.type]);
  const isGoalkeeper = useMemo(() => ['GOALKEEPER', 'GOALKEEPER_CAPTAIN'].includes(lnPlayer.type), [lnPlayer.type]);
  useFitty(playerNameRef, { minSize: 2, multiLine: true });

  const playerName = useMemo(() => {
    let result = player.shortName;
    if (isField) return result;

    if (isGoalkeeper) result += ` (${gk})`;
    if (isCaptain) result += ` (${c})`;
    return result;
  }, [c, gk, isCaptain, isField, isGoalkeeper, player.shortName]);

  const substitutions = getPlayerSubstitutions(player, events, isBench);
  const cards = getPlayerCards(player, events);

  const goals = getPlayerGoals(player, events).goalsNumber;
  const ownGoals = getPlayerGoals(player, events).ownGoalsNumber;

  return (
    <PkIdentifier
      className={clsx(classes.playerIdentifier, isGoalkeeper && classes.isGoalkeeper)}
      type={identifierType}
    >
      <span slot='prefix' className='pk-mb--0'>
        <PkJersey
          number={lnPlayer.jerseyNumber}
          shirtColor={teamColorShirt}
          substitution={substitutions}
          goals={goals}
          ownGoals={ownGoals}
          cards={cards}
          captainLabel={isCaptain ? c : null}
          size={27}
        />
      </span>
      <div slot='primary' className='pk-w--100'>
        <span ref={playerNameRef}>{playerName}</span>
      </div>
    </PkIdentifier>
  );
};

const TeamPitch = ({
  teamLineup,
  events,
  match,
  disablePlayerLink,
}: {
  teamLineup: LineupsTeam;
  events: PlayerEvent[];
  match: Match;
  disablePlayerLink: boolean;
}) => {
  return (
    <div className={classes.pitch__team}>
      {teamLineup.field.filter(getPlayerPosition).map((p) => (
        <div className={classes.pitchTeamPlayer} key={p.player.id} style={getPlayerPosition(p)}>
          <LineupPlayerLinked
            lnPlayer={p}
            isField
            events={events}
            match={match}
            teamColorShirt={teamLineup.shirtColor ?? '#fff'}
            identifierType='vertical'
            disablePlayerLink={disablePlayerLink}
          />
        </div>
      ))}
    </div>
  );
};

const PlayerList = ({
  teamLineup,
  events,
  showTeam,
  lineupType = 'field',
  coachLabel,
  match,
  disablePlayerLink,
}: {
  teamLineup: LineupsTeam;
  events: PlayerEvent[];
  showTeam?: boolean;
  lineupType?: 'bench' | 'field' | 'unavailable';
  coachLabel?: string;
  match: Match;
  disablePlayerLink: boolean;
}) => {
  const playerList = useMemo(() => teamLineup[lineupType], [teamLineup, lineupType]);

  const isSuspendedList = lineupType === 'unavailable';

  return (
    <div className={clsx(classes.playerlist)}>
      {showTeam && (
        <TeamIdentifier
          className={clsx(classes.playerlistTeam, 'pk-py--0')}
          team={teamLineup?.team}
          showTeamCountry={false}
          customSecondaryText={coachLabel}
          badgeSize={32}
        />
      )}

      <ul className={clsx(classes.playerlistList, isSuspendedList && classes.suspended)}>
        {playerList.map((p) => (
          <li key={p.player.id} className={classes.playerlistPlayer}>
            <LineupPlayerLinked
              lnPlayer={p}
              events={events}
              isBench={lineupType === 'bench'}
              match={match}
              teamColorShirt={teamLineup.shirtColor ?? '#fff'}
              identifierType='horizontal'
              disablePlayerLink={disablePlayerLink}
            />
          </li>
        ))}
      </ul>
    </div>
  );
};

const LineupsComponent = ({
  options,
  isLiveblog = false,
}: {
  options: {
    match?: Match;
    interval?: number;
    lineupsBanner?: string;
    isFemaleCompetition?: boolean;
    disablePlayerLink?: boolean;
  };
  isLiveblog?: boolean;
}) => {
  const t = useTranslateFn();
  const match = useMatch(options.match);

  const pollInterval = useMemo(
    () => (options.interval < 0 ? options.interval : getRefreshRate(match)),
    [options.interval, match],
  );

  const { data: lineups } = useQuery({
    ...getMatchLineupsQuery(match.id),
    refetchInterval: !IS_TEST && pollInterval,
    enabled: !!match?.id,
  });

  const homeLineup = useMemo(() => lineups?.homeTeam, [lineups]);
  const awayLineup = useMemo(() => lineups?.awayTeam, [lineups]);

  const { data: events } = useQuery({
    ...getMatchEventsQuery(match.id, {
      filter: EventsFilter.LINEUP,
    }),
    refetchInterval: !IS_TEST && pollInterval,
  });

  const { data: unavailablePlayers } = useQuery({
    ...getMatchPlayersStatusQuery(match.id, 'SUSPENDED'),
    enabled: !!match?.id && !!homeLineup?.team && !!awayLineup?.team,
  });

  const unavailableLineup = useMemo(() => {
    if (!unavailablePlayers?.length) return null;

    const isClubMatch = match.competition.teamCategory === 'CLUB';
    const { awayUnavailable, homeUnavailable } = splitUnavailablePlayersToTeams({
      awayLineup,
      homeLineup,
      isClubMatch,
      unavailablePlayers,
    });
    return { away: awayUnavailable, home: homeUnavailable };
  }, [awayLineup, homeLineup, match, unavailablePlayers]);

  const substitutions = useMemo(() => getEventsByTypes(events, [EventType.SUBSTITUTION]), [events]);
  const homeEvents = useMemo(
    () => events?.filter((e) => e.primaryActor.team?.id === homeLineup?.team?.id),
    [events, homeLineup?.team],
  );
  const awayEvents = useMemo(
    () => events?.filter((e) => e.primaryActor.team?.id === awayLineup?.team?.id),
    [events, awayLineup?.team],
  );

  const homeCoach = useMemo(() => {
    const name = getCoachPerson(homeLineup)?.name;
    const role = getCoachRole(homeLineup)?.role;
    return name ? `${role}: ${name}` : null;
  }, [homeLineup]);

  const awayCoach = useMemo(() => {
    const name = getCoachPerson(awayLineup)?.name;
    const role = getCoachRole(awayLineup)?.role;
    return name ? `${role}: ${name}` : null;
  }, [awayLineup]);

  const showPitch = useMemo(() => showTacticalLineups(match, homeLineup, awayLineup), [awayLineup, homeLineup, match]);

  return (
    <>
      {homeLineup?.team && awayLineup?.team && (
        <div
          className={clsx(
            classes.matchLineups,
            showPitch && classes.withTactical,
            isLiveblog && classes.containerLiveblog,
          )}
          data-tracking={JSON.stringify(trackingData)}
        >
          {showPitch ? (
            <div className={classes.pitchContainer}>
              <div className={classes.teamWrap}>
                <TeamIdentifier
                  team={match.homeTeam}
                  showTeamCountry={false}
                  customSecondaryText={homeCoach}
                  badgeSize={32}
                  className='pk-py--xs'
                />
              </div>
              <div className={classes.lineup__pitch}>
                <TeamPitch
                  teamLineup={homeLineup}
                  events={homeEvents}
                  match={match}
                  disablePlayerLink={options.disablePlayerLink}
                />
                <TeamPitch
                  teamLineup={awayLineup}
                  events={awayEvents}
                  match={match}
                  disablePlayerLink={options.disablePlayerLink}
                />
              </div>
              <div className={clsx(classes.teamWrap, classes.teamWrapAway)}>
                <TeamIdentifier
                  team={match.awayTeam}
                  showTeamCountry={false}
                  alignment='right'
                  badgeSlot='suffix'
                  customSecondaryText={awayCoach}
                  badgeSize={32}
                  className='pk-py--xs'
                />
              </div>
            </div>
          ) : (
            <div className={classes.playerlistContainer}>
              <div className={classes.playerlistWrap}>
                <PlayerList
                  teamLineup={homeLineup}
                  events={homeEvents}
                  showTeam
                  match={match}
                  coachLabel={homeCoach}
                  disablePlayerLink={options.disablePlayerLink}
                />
                <PlayerList
                  teamLineup={awayLineup}
                  events={awayEvents}
                  showTeam
                  match={match}
                  coachLabel={awayCoach}
                  disablePlayerLink={options.disablePlayerLink}
                />
              </div>
            </div>
          )}
          {!isLiveblog && (
            <>
              {!!options.lineupsBanner && match.status === MatchStatus.UPCOMING && (
                <div className={classes.banner}>
                  <div className={classes.bannerLabel}>{t('lineups-banner-label')}</div>
                  <GptBanner
                    className={classes.bannerSlot}
                    slotType='leaderboard-small'
                    adUnitPath={`/5874/${window.competitionBanner}.{language}/${options.lineupsBanner}`}
                    position='top'
                  />
                </div>
              )}
              <SubstitutionCarousel
                substitutions={substitutions}
                enableLink={!options.disablePlayerLink}
                match={match}
              />
              {(!!homeLineup?.bench?.length || !!awayLineup?.bench?.length) && (
                <div className={classes.playerlistContainer}>
                  <h3 className={classes.playerlistTitle}>
                    {t(`substitutes${options.isFemaleCompetition ? '_f' : ''}`)}
                  </h3>
                  <div className={classes.playerlistWrap}>
                    <PlayerList
                      teamLineup={homeLineup}
                      events={homeEvents}
                      lineupType='bench'
                      showTeam={showPitch}
                      match={match}
                      disablePlayerLink={options.disablePlayerLink}
                    />
                    <PlayerList
                      teamLineup={awayLineup}
                      events={awayEvents}
                      lineupType='bench'
                      showTeam={showPitch}
                      match={match}
                      disablePlayerLink={options.disablePlayerLink}
                    />
                  </div>
                </div>
              )}
              {Boolean(unavailableLineup) && (
                <div className={classes.playerlistContainer}>
                  <h3 className={classes.playerlistTitle}>{t('player_suspended')}</h3>
                  <div className={classes.playerlistWrap}>
                    <PlayerList
                      teamLineup={{ ...homeLineup, unavailable: unavailableLineup.home }}
                      events={homeEvents}
                      lineupType='unavailable'
                      showTeam={false}
                      match={match}
                      disablePlayerLink={options.disablePlayerLink}
                    />
                    <PlayerList
                      teamLineup={{ ...awayLineup, unavailable: unavailableLineup.away }}
                      events={awayEvents}
                      lineupType='unavailable'
                      showTeam={false}
                      match={match}
                      disablePlayerLink={options.disablePlayerLink}
                    />
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </>
  );
};

export default memo(LineupsComponent);
